import { useEffect, useMemo, useState } from 'react';
import {
  bulkSanctions,
  getSanctionStats,
  listPending,
  listSanctions,
  revertSanction,
  type CreateSanctionInput,
  type PendingReviewRow,
  type Sanction,
  type SanctionStats,
} from '../../services/sanctions';

type Tab = 'pending' | 'history';

function sanctionTarget(pending: PendingReviewRow): {
  kind: CreateSanctionInput['target_kind'];
  value: string;
} {
  if (pending.account_id) return { kind: 'account', value: pending.account_id };
  if (pending.hwid) return { kind: 'hwid', value: pending.hwid };
  return { kind: 'session_uuid', value: pending.session_uuid };
}

function SanctionTypeBadge({ type }: { type: Sanction['sanction_type'] }) {
  const colors: Record<Sanction['sanction_type'], string> = {
    ban_perm: 'bg-red-900 text-red-300 border-red-700',
    ban_temp: 'bg-orange-900 text-orange-300 border-orange-700',
    kick:     'bg-amber-900 text-amber-300 border-amber-700',
    warn:     'bg-blue-900 text-blue-300 border-blue-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${colors[type]}`}>
      {type}
    </span>
  );
}

function StatCell({ label, value, sub, accent }: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'normal' | 'warn' | 'danger';
}) {
  const color =
    accent === 'danger' ? 'text-red-300'   :
    accent === 'warn'   ? 'text-amber-300' :
    'text-gray-100';
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</div>
      <div className={`text-xl font-bold ${color} tabular-nums mt-0.5`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function Sanctions() {
  const [tab, setTab] = useState<Tab>('pending');
  const [pending, setPending] = useState<PendingReviewRow[]>([]);
  const [history, setHistory] = useState<Sanction[]>([]);
  const [includeReverted, setIncludeReverted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [riskThreshold, setRiskThreshold] = useState(50);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [stats, setStats] = useState<SanctionStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSanctionStats()
      .then((r) => { if (!cancelled) setStats(r); })
      .catch(() => {
        // Stats falhar não derruba a página — silencioso.
        if (!cancelled) setStats(null);
      });
    return () => { cancelled = true; };
  }, [refreshTick]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (tab === 'pending') {
      listPending(riskThreshold, 100)
        .then((r) => { if (!cancelled) setPending(r); })
        .catch((e) => { if (!cancelled) setError(e?.message || 'erro'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      listSanctions(includeReverted, 100)
        .then((r) => { if (!cancelled) setHistory(r); })
        .catch((e) => { if (!cancelled) setError(e?.message || 'erro'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [tab, includeReverted, refreshTick, riskThreshold]);

  const selectableCount = useMemo(
    () => new Set(
      pending
        .filter((p) => !p.already_sanctioned)
        .map((p) => {
          const target = sanctionTarget(p);
          return `${target.kind}:${target.value}`;
        }),
    ).size,
    [pending],
  );

  const selectedTargetCount = useMemo(
    () => new Set(
      pending
        .filter((p) => selected.has(p.session_uuid) && !p.already_sanctioned)
        .map((p) => {
          const target = sanctionTarget(p);
          return `${target.kind}:${target.value}`;
        }),
    ).size,
    [pending, selected],
  );

  function toggleSelect(uuid: string): void {
    const next = new Set(selected);
    if (next.has(uuid)) next.delete(uuid);
    else next.add(uuid);
    setSelected(next);
  }

  async function handleBulkSanction(sanctionType: 'ban_temp' | 'ban_perm'): Promise<void> {
    if (selected.size === 0) return;
    const uniqueTargets = new Map<string, { pending: PendingReviewRow; target: ReturnType<typeof sanctionTarget> }>();
    for (const row of pending) {
      if (!selected.has(row.session_uuid) || row.already_sanctioned) continue;
      const target = sanctionTarget(row);
      const key = `${target.kind}:${target.value}`;
      if (!uniqueTargets.has(key)) uniqueTargets.set(key, { pending: row, target });
    }
    if (uniqueTargets.size === 0) {
      window.alert('Nenhuma sessao selecionada tem identidade valida');
      return;
    }
    const isTemporary = sanctionType === 'ban_temp';
    const actionLabel = isTemporary ? 'suspensão de 24 horas' : 'ban permanente';
    const reason = window.prompt(
      `Motivo da ${actionLabel} (será aplicado em todas):`,
      isTemporary ? 'suspensão manual por 24h' : 'ban wave manual',
    );
    if (!reason) return;
    if (!window.confirm(
      `Confirma ${actionLabel} em ${uniqueTargets.size} contas/alvos únicos?`,
    )) return;

    setBulkBusy(true);
    try {
      const inputs: CreateSanctionInput[] = [...uniqueTargets.values()]
        .map(({ pending: row, target }) => ({
            sanction_type: sanctionType,
            target_kind: target.kind,
            target_value: target.value,
            reason,
            source_session_uuid: row.session_uuid,
            active_until_epoch: isTemporary
              ? Math.floor(Date.now() / 1000) + (24 * 60 * 60)
              : undefined,
          }));
      const result = await bulkSanctions(inputs);
      window.alert(
        `Ban wave aplicado:\n` +
        `  Created: ${result.created}\n` +
        `  Skipped (duplicates): ${result.skipped_duplicate}\n` +
        `  Failed: ${result.failed}`,
      );
      setSelected(new Set());
      setRefreshTick((t) => t + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'erro';
      window.alert(`Falha: ${msg}`);
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleRevert(s: Sanction): Promise<void> {
    const reason = window.prompt(`Motivo do revert pra ${s.target_kind}=${s.target_value}:`, '');
    if (reason === null) return;
    try {
      await revertSanction(s.id, reason);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'erro';
      window.alert(`Falha: ${msg}`);
    }
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-200">Sanctions</h2>
          <p className="text-xs text-gray-500 mt-1">
            Revisão por pico histórico: a conta continua pendente mesmo depois que o risco atual decai.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setTab('pending')}
            className={`px-3 py-1.5 text-sm rounded border ${
              tab === 'pending'
                ? 'border-red-700 bg-red-950/40 text-red-300'
                : 'border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-gray-800'
            }`}
          >
            Pending review
          </button>
          <button
            type="button"
            onClick={() => setTab('history')}
            className={`px-3 py-1.5 text-sm rounded border ${
              tab === 'history'
                ? 'border-red-700 bg-red-950/40 text-red-300'
                : 'border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-gray-800'
            }`}
          >
            Histórico
          </button>
          <button
            type="button"
            onClick={() => setRefreshTick((t) => t + 1)}
            className="px-3 py-1.5 text-sm rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-gray-800"
          >
            Refresh
          </button>
        </div>
      </header>

      {stats && (
        <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 mb-4">
          <div className="text-xs uppercase text-gray-500 tracking-wide mb-3">
            Ban wave stats
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCell
              label="Total"
              value={stats.total_count}
            />
            <StatCell
              label="Ativas"
              value={stats.active_count}
              accent="danger"
            />
            <StatCell
              label="Revertidas"
              value={stats.reverted_count}
              accent="warn"
            />
            <StatCell
              label="Criadas (24h)"
              value={stats.created_last_24h}
              sub={stats.reverted_last_24h > 0 ? `${stats.reverted_last_24h} revertidas no período` : undefined}
            />
            <StatCell
              label="FP rate"
              value={stats.fp_rate !== undefined ? `${(stats.fp_rate * 100).toFixed(1)}%` : '—'}
              sub="revertidas / total"
              accent={stats.fp_rate !== undefined && stats.fp_rate >= 0.1 ? 'warn' : 'normal'}
            />
            <StatCell
              label="Top tipo"
              value={
                stats.by_sanction_type.length > 0
                  ? `${stats.by_sanction_type[0].sanction_type} (${stats.by_sanction_type[0].count})`
                  : '—'
              }
              sub={
                stats.by_target_kind.length > 0
                  ? `target top: ${stats.by_target_kind[0].target_kind}`
                  : undefined
              }
            />
          </div>
          {stats.fp_rate !== undefined && stats.fp_rate >= 0.15 && (
            <div className="mt-3 text-xs text-amber-400">
              ⚠️ FP rate &gt;= 15% — considerar calibragem do risk threshold ou revisão de ruleset.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {tab === 'pending' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-400">
              Risk threshold &gt;
              <input
                type="number"
                min={0}
                max={99}
                value={riskThreshold}
                onChange={(e) => setRiskThreshold(parseInt(e.target.value, 10) || 0)}
                className="w-16 rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
              />
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {selected.size} sessões selecionadas • {selectedTargetCount} contas/alvos únicos • {selectableCount} elegíveis
              </span>
              <button
                type="button"
                onClick={() => handleBulkSanction('ban_temp')}
                disabled={selected.size === 0 || bulkBusy}
                className="px-4 py-2 text-sm rounded border border-amber-700 bg-amber-900/30 text-amber-200 hover:bg-amber-800/40 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {bulkBusy ? '...' : `Suspender 24h (${selectedTargetCount})`}
              </button>
              <button
                type="button"
                onClick={() => handleBulkSanction('ban_perm')}
                disabled={selected.size === 0 || bulkBusy}
                className="px-4 py-2 text-sm rounded border border-red-700 bg-red-900/40 text-red-200 hover:bg-red-800/40 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {bulkBusy ? '...' : `Banir permanente (${selectedTargetCount})`}
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-gray-500">Carregando...</p>
          ) : pending.length === 0 ? (
            <p className="text-gray-500 text-sm">Nenhuma sessão pendente acima do threshold.</p>
          ) : (
            <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-2 py-2 w-10"></th>
                    <th className="text-left px-4 py-2">Session</th>
                    <th className="text-left px-4 py-2">Conta</th>
                    <th className="text-left px-4 py-2">HWID</th>
                    <th className="text-right px-4 py-2">Atual / pico</th>
                    <th className="text-right px-4 py-2">Infrações</th>
                    <th className="text-left px-4 py-2">Decisão pico</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2f36]">
                  {pending.map((p) => (
                    <tr key={p.session_uuid} className="hover:bg-[#0d0f12]">
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          disabled={p.already_sanctioned}
                          checked={selected.has(p.session_uuid)}
                          onChange={() => toggleSelect(p.session_uuid)}
                        />
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-400" title={p.session_uuid}>
                        {p.session_uuid.slice(0, 8)}…
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-200">{p.account_id || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-300">{p.hwid || '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-200 tabular-nums">
                        {p.risk_score} / <strong className="text-orange-300">{p.peak_risk_score}</strong>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-300" title={`${p.occurrence_sum} ocorrências agregadas`}>
                        {p.incident_count} <span className="text-gray-600">/ ×{p.occurrence_sum}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-300">{p.peak_decision_real}</td>
                      <td className="px-4 py-2 text-xs">
                        {p.already_sanctioned ? (
                          <span className="text-red-400">já sancionado</span>
                        ) : (
                          <span className="text-emerald-400">pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={includeReverted}
              onChange={(e) => setIncludeReverted(e.target.checked)}
            />
            Incluir revertidas
          </label>
          {loading ? (
            <p className="text-gray-500">Carregando...</p>
          ) : history.length === 0 ? (
            <p className="text-gray-500 text-sm">Nenhuma sanction.</p>
          ) : (
            <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Tipo</th>
                    <th className="text-left px-4 py-2">Target kind</th>
                    <th className="text-left px-4 py-2">Target</th>
                    <th className="text-left px-4 py-2">Reason</th>
                    <th className="text-left px-4 py-2">Created</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2f36]">
                  {history.map((s) => (
                    <tr key={s.id}>
                      <td className="px-4 py-2"><SanctionTypeBadge type={s.sanction_type} /></td>
                      <td className="px-4 py-2 text-xs text-gray-300">{s.target_kind}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-300">{s.target_value}</td>
                      <td className="px-4 py-2 text-xs text-gray-400" title={s.reason}>
                        {s.reason.slice(0, 60)}{s.reason.length > 60 ? '…' : ''}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-400">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {s.is_active ? (
                          <span className="text-red-400">● ativa</span>
                        ) : (
                          <span className="text-gray-500">revertida</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {s.is_active ? (
                          <button
                            type="button"
                            onClick={() => handleRevert(s)}
                            className="px-2 py-1 text-xs rounded border border-amber-800 bg-amber-950/40 text-amber-300 hover:bg-amber-900/40"
                          >
                            Revert
                          </button>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
